import React, { useState } from "react";
import { Modal, Button, Card, Row, Col, Typography, Tag } from "antd";
import { DownloadOutlined, PlayCircleOutlined, AudioOutlined, FileTextOutlined } from "@ant-design/icons";
import moment from "moment";
import { capitalizeFirstLetter } from "../../../../utils/utils";
import ModelWrapper from "../../../modal/ModelWrapper";
import { CustomSecondaryButton } from "../styled";


const { Text, Title } = Typography;

const FileSelectionModal = ({
  visible,
  onCancel,
  onSelect,
  recording,
  action = "download", 
  getRecordingFileFromS3,
  saveRecording,
  setVisible
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileSelect = async (fileType, fileData) => {
    setIsLoading(true);
    setSelectedFile({ type: fileType, data: fileData });
    
    try {
      if (action === "download") {
        if (fileType === "original") {
          await saveRecording(recording.recording);
        } else if (fileType === "cloned") {
          await saveRecording(recording.clonedFiles.fileUrl);
        }
        onSelect(fileType, fileData);
      } else if (action === "play") {
        onSelect(fileType, fileData);
      }
    } catch (error) {
      console.error("Error processing file:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFileInfo = (fileType) => {
    if (fileType === "original") {
      return {
        title: "Original Recording",
        name: recording.name,
        createdAt: recording.createdAt,
        status: recording.status,
        uploadedBy: recording.user?.name || "Unknown",
        icon: <AudioOutlined style={{ fontSize: "24px", color: "#8A3EF4" }} />
      };
    } else if (fileType === "cloned") {
      return {
        title: "Cloned Recording",
        name: recording.clonedFiles.name,
        createdAt: recording.clonedFiles.uploadedBy ? recording.createdAt : "Recently uploaded",
        status: "cloned",
        uploadedBy: "Cloned version",
        comment: recording.clonedFiles.comment,
        icon: <FileTextOutlined style={{ fontSize: "24px", color: "#52c41a" }} />
      };
    }
  };

  const originalFileInfo = getFileInfo("original");
  const clonedFileInfo = getFileInfo("cloned");

  return (
    <ModelWrapper
      title={
        <div className="gap-12">
          {action === "download" ? (
            <DownloadOutlined style={{ fontSize: "20px", color: "#8A3EF4" }} />
          ) : (
            <PlayCircleOutlined style={{ fontSize: "20px", color: "#8A3EF4" }} />
          )}
          <span className="font-18 font-semibold text-primary" style={{marginLeft: "10px"}}>
            Select File to {action === "download" ? "Download" : "Play"}
          </span>
        </div>
      }
      show={visible}
      setShow={setVisible}
      onCancel={onCancel}
      footer={null}
      width={700}
      centered
      destroyOnClose
    >
      <div className="mb-24">
        <Text className="font-14 text-secondry">
          Choose which version of the recording you want to {action}:
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        {/* Original File Card */}
        <Col span={12}>
          <Card
            hoverable
            className={`file-selection-card ${selectedFile?.type === "original" ? "selected" : ""}`}
            onClick={() => handleFileSelect("original", recording)}
            style={{
              border: selectedFile?.type === "original" ? "2px solid #8A3EF4" : "1px solid var(--border-color)",
              borderRadius: "12px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              backgroundColor: selectedFile?.type === "original" ? "#f0f2ff" : "var(--primary-bg)"
            }}
            bodyStyle={{ padding: "20px" }}
          >
            <div className="d-flex align-items-center justify-start gap-12 mb-16">
              {originalFileInfo.icon}
              <div>
                <Title level={5} style={{ margin: 0, color: "var(--primary-text-color)" }}>
                  {originalFileInfo.title}
                </Title>
                <Tag color="blue" style={{ margin: 0, fontSize: "11px" }}>
                  Original
                </Tag>
              </div>
            </div>

            <div className="mb-12" style={{marginTop: "10px"}}>
              <Text className="font-14 font-semibold text-primary">
                {capitalizeFirstLetter(originalFileInfo.name)}
              </Text>
            </div>

            <div className="mb-8">
              <Text className="font-12 text-secondry">
                Recorded by: {originalFileInfo.uploadedBy}
              </Text>
            </div>

            <div className="mb-8">
              <Text className="font-12 text-secondry">
                Created: {moment(originalFileInfo.createdAt).format("DD MMM YYYY hh:mm A")}
              </Text>
            </div>

            <div>
              <Tag
                color={
                  originalFileInfo.status === "approved" ? "success" :
                  originalFileInfo.status === "pending" ? "processing" :
                  originalFileInfo.status === "rejected" ? "error" : "default"
                }
                style={{ fontSize: "11px" }}
              >
                {capitalizeFirstLetter(originalFileInfo.status)}
              </Tag>
            </div>
          </Card>
        </Col>

        {/* Cloned File Card */}
        <Col span={12}>
          <Card
            hoverable
            className={`file-selection-card ${selectedFile?.type === "cloned" ? "selected" : ""}`}
            onClick={() => handleFileSelect("cloned", recording.clonedFiles)}
            style={{
              border: selectedFile?.type === "cloned" ? "2px solid #52c41a" : "1px solid var(--border-color)",
              borderRadius: "12px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              backgroundColor: selectedFile?.type === "cloned" ? "#f6ffed" : "var(--primary-bg)"
            }}
            bodyStyle={{ padding: "20px" }}
          >
            <div className="d-flex align-items-center justify-start gap-12 mb-16">
              {clonedFileInfo.icon}
              <div>
                <Title level={5} style={{ margin: 0, color: "var(--primary-text-color)" }}>
                  {clonedFileInfo.title}
                </Title>
                <Tag color="green" style={{ margin: 0, fontSize: "11px" }}>
                  Cloned
                </Tag>
              </div>
            </div>

            <div className="mb-12" style={{marginTop: "10px"}}>
              <Text className="font-14 font-semibold text-primary">
                {capitalizeFirstLetter(clonedFileInfo.name)}
              </Text>
            </div>

            <div className="mb-8">
              <Text className="font-12 text-secondry">
                Uploaded by: {clonedFileInfo.uploadedBy}
              </Text>
            </div>

            {clonedFileInfo.comment && (
              <div className="mb-8">
                <Text className="font-12 text-secondry">
                  Comment: {clonedFileInfo.comment}
                </Text>
              </div>
            )}

            <div>
              <Tag color="green" style={{ fontSize: "11px" }}>
                Cloned Version
              </Tag>
            </div>
          </Card>
        </Col>
      </Row>

      <div className="d-flex justify-end gap-12 mt-24">
        <CustomSecondaryButton onClick={onCancel} disabled={isLoading}>
          Cancel
        </CustomSecondaryButton>
      </div>

      <style jsx>{`
        .file-selection-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .file-selection-card.selected {
          box-shadow: 0 4px 12px rgba(138, 62, 244, 0.2);
        }
      `}</style>
    </ModelWrapper>
  );
};

export default FileSelectionModal; 